import NumberFlow from '@number-flow/react'
import type { SetStateAction } from 'jotai'
import { AnimatePresence, motion } from 'motion/react'
import {
  type CSSProperties,
  type Dispatch,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import { useTranslation } from 'react-i18next'
import type { ValueOf } from 'type-fest'
import IcBaselineExpandMore from '~icons/ic/baseline-expand-more'
import { UIButton } from 'src/components/ui/Button'
import Text from 'src/components/ui/Text'
import { MembershipCardState } from 'src/features/Membership/components/MembershipPlanCard/stateMachine'
import { getMembershipPlanDiscountPercentage } from 'src/features/Membership/utils/membershipPlanDiscount'
import { getMembershipTierConfig } from 'src/features/Membership/utils/subscriptionStateUtils'
import { useMyInfo } from 'src/services/apollo/auth'
import { Currency, Interval } from 'src/services/apollo/generated/graphql'
import { useMyMembership } from 'src/services/apollo/membership'
import {
  type MembershipPlan,
  type MembershipSubscribeActionArgs,
} from 'src/services/helpers/membership'
import { useCurrency } from 'src/utils/currency'
import { cx } from 'src/utils/cx'
import PriceTag from '~/components/PriceTag'
import { MembershipCardButton } from './MembershipCardButton'
import type { SubscriptionInfo } from './useMembershipCardActions'
import { useMembershipCardActions } from './useMembershipCardActions'
import {
  type CardStateResult,
  useMembershipCardState,
} from './useMembershipCardState'

const motionLiProps = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
}

function ExpandedPrivilegeListItem(props: PropsWithChildren) {
  return <motion.li {...motionLiProps}>{props.children}</motion.li>
}

function MembershipNewFeatureBadge() {
  return (
    <p className="absolute right-0 top-0 rounded-bl-lg pl-2 pr-1 py-[2px] leading-none text-white bg-gradient-purple-pink text-[10px]">
      New
    </p>
  )
}

function MembershipPriceAndLabel(props: {
  label: string
  prices: ValueOf<ValueOf<MembershipPlan['prices']>>
  currency: Currency
  interval: Interval
  tier: number
  featured?: boolean
  discountComponent?: (props: { discount: number }) => ReactNode
  cardState: CardStateResult
}) {
  const { t } = useTranslation(['membership'])
  const currency = props.currency || Currency.Usd

  const {
    perMonth = 0,
    perYear = 0,
    perMonthOriginal = 0,
    perYearOriginal = 0,
  } = props.prices

  const primaryNumber =
    props.interval === Interval.Month ? perMonth : perYear / 12
  const primaryNumberBeforeDiscount =
    props.interval === Interval.Month ? perMonthOriginal : perYearOriginal / 12

  const discountPercentage = getMembershipPlanDiscountPercentage(
    props.prices,
    props.interval,
  )
  const hasDiscount = discountPercentage != null
  const primaryUnit = t('membership:membership.price-tag.per-month')

  const chargedText = () => {
    if (props.interval === Interval.Month) {
      return t('membership:membership.price-tag.charged-monthly')
    }

    return (
      <>
        <p className="flex gap-1">
          <PriceTag currency={currency} value={perYear} />
          <span>{t('membership:membership.price-tag.charged-annually')}</span>
        </p>
        {props.cardState.state === MembershipCardState.UPGRADE_AVAILABLE && (
          <p className="mt-1">
            <Text i18nKey="membership:membership.price-tag.billing-fix-included" />
          </p>
        )}
      </>
    )
  }

  return (
    <motion.div
      className={cx(
        'p-4 mlg:pl-8 text-black dark:text-white dark:bg-zinc-800',
        '[--from:transparent] [--to:var(--plan-bg-color)]',
        'dark:[--from:var(--plan-bg-color-dark)] dark:[--to:transparent]',
        !props.featured && 'rounded-t-2xl',
      )}
      style={{
        background:
          'radial-gradient(100% 100% at 0% 0%, var(--from) 30%, var(--to))',
      }}
      layout
    >
      <p className="-mb-1 flex items-center text-lg font-light uppercase tracking-wider">
        <Text i18nKey={props.label} />
        {discountPercentage && (
          <span className="ml-2 inline-flex items-center h-0">
            <span
              className={cx(
                'relative ml-0 px-1.5 text-sm tabular-nums text-white rounded-full tracking-tight',
                'bg-green-700',
              )}
            >
              {props.discountComponent?.({
                discount: discountPercentage,
              }) ?? `-${discountPercentage}%`}
            </span>
          </span>
        )}
      </p>
      {/* Original price */}
      {hasDiscount && (
        <p className="font-light text-lg leading-[1.05] text-black line-through decoration-2 dark:text-white opacity-50 mt-3 -mb-3">
          <PriceTag currency={currency} value={primaryNumberBeforeDiscount} />
        </p>
      )}
      {/* Present price */}
      <div className="my-3 flex flex-col gap-y-0.5">
        <p className="text-lg leading-[1.05] font-medium whitespace-nowrap">
          <PriceTag
            currency={currency}
            value={primaryNumber}
            className="tabular-nums"
            renderNumberPart={({ value, locale, format }) => (
              <NumberFlow
                value={value}
                locales={locale}
                format={format}
                className="text-4xl leading-[1.05]"
              />
            )}
          />
          <span className="leading-none whitespace-nowrap">
            &nbsp;{primaryUnit}
          </span>
        </p>
        <div className="text-sm leading-none text-neutral-600 dark:text-neutral-400">
          {chargedText()}
        </div>
      </div>
    </motion.div>
  )
}

function MembershipPrivilegesList(props: {
  privileges: MembershipPlan['privileges']
  expanded?: boolean
  setExpanded?: Dispatch<SetStateAction<boolean>>
}) {
  const { t } = useTranslation(['membership'])
  const highlightFeatureClass =
    'relative text-transparent bg-clip-text bg-gradient-purple-pink !pr-8 mlg:!pr-9'

  return (
    <>
      <ul className="flex-1 text-sm grid auto-rows-[minmax(3em,1fr)] border-b border-black/10 dark:border-white/5 divide-y divide-black/10 dark:divide-white/5 [&_li]:px-4 mlg:[&_li]:px-8 [&_li]:py-1 [&_li]:flex [&_li]:flex-col [&_li]:justify-center">
        <li className={highlightFeatureClass}>
          <Text i18nKey="membership:membership.privilege.video-generation" />
          <MembershipNewFeatureBadge />
        </li>
        {props.privileges.professionalMode && (
          <li className={highlightFeatureClass}>
            <Text i18nKey="membership:membership.privilege.professional-mode" />
            <MembershipNewFeatureBadge />
          </li>
        )}
        <li className={highlightFeatureClass}>
          {t('membership:membership.privilege.turbo-mode')}
          <MembershipNewFeatureBadge />
        </li>
        <li>
          {t('membership:membership.privilege.early-access-model-free-try')}
        </li>
        <li>
          {t('membership:membership.privilege.extra-lora-training', {
            amount: props.privileges.freeUserLora ?? '…',
          })}
        </li>
        <li>
          {t('membership:membership.privilege.extra-lora-usage', {
            amount: props.privileges.lora ?? '…',
          })}
        </li>
        <li>
          {t('membership:membership.privilege.cheaper-topup2', {
            amount: `${
              (Number(props.privileges.extraPackageValue) || 100000) / 1000 -
              100
            }%`,
          })}
        </li>
        <li>
          {t('membership:membership.privilege.more-credits-to-claim', {
            amount: `${
              (Number(props.privileges.dailyClaimAdded) || 10000) / 100
            }%`,
          })}
        </li>
        <li>
          {t('membership:membership.privilege.credits-per-month', {
            amount: props.privileges?.paidCredit?.toLocaleString(),
          })}
        </li>

        <AnimatePresence mode="wait">
          {props.expanded && (
            <>
              <ExpandedPrivilegeListItem>
                {t('membership:membership.privilege.private-models', {
                  amount: props.privileges.privateModel ?? '…',
                })}
              </ExpandedPrivilegeListItem>
              <ExpandedPrivilegeListItem>
                {t('membership:membership.privilege.membership-badge')}
              </ExpandedPrivilegeListItem>
              <ExpandedPrivilegeListItem>
                {t(
                  'membership:membership.privilege.profile-banner-and-gif-avatar',
                )}
              </ExpandedPrivilegeListItem>
              <ExpandedPrivilegeListItem>
                {t('membership:membership.privilege.larger-image-dimensions')}
              </ExpandedPrivilegeListItem>
              <ExpandedPrivilegeListItem>
                {t('membership:membership.privilege.custom-model-list')}
              </ExpandedPrivilegeListItem>
              <ExpandedPrivilegeListItem>
                {t('membership:membership.privilege.custom-quality-tags')}
              </ExpandedPrivilegeListItem>
              <ExpandedPrivilegeListItem>
                {t('membership:membership.privilege.more-coming-soon')}
              </ExpandedPrivilegeListItem>
            </>
          )}
        </AnimatePresence>
      </ul>
      {!props.expanded && (
        <UIButton
          variant="ghost"
          className="flex items-center justify-center w-full rounded-none focus:outline-none border-b border-black/10 dark:border-white/5"
          onPress={() => {
            props.setExpanded?.(prev => !prev)
          }}
        >
          <IcBaselineExpandMore className="size-6" />
        </UIButton>
      )}
    </>
  )
}

function FeaturedBadge() {
  const { t } = useTranslation(['membership'])
  return (
    <div className="h-8 flex justify-center items-center text-xs uppercase tracking-wider font-medium bg-[--accent-color] text-[--accent-color-text] rounded-t-2xl">
      {t('membership:membership.featured-badge.label')}
    </div>
  )
}

export interface MembershipPlanCardNewProps
  extends MembershipPlan, MembershipSubscribeActionArgs {
  className?: string
  style?: CSSProperties
  interval: Interval
  featured?: boolean
  enableCancel?: boolean
  discountComponent?: (props: { discount: number }) => ReactNode
  shouldDiscount?: boolean
  trackingArgs?: Record<string, any>
  expanded?: boolean
  setExpanded?: Dispatch<SetStateAction<boolean>>
}

export default function MembershipPlanCardNew(
  props: MembershipPlanCardNewProps,
) {
  const [currency] = useCurrency()
  const { data: me } = useMyInfo()
  const { data: { membership, subscription } = {}, refetch } = useMyMembership()

  useEffect(() => {
    refetch()
  }, [me, refetch])

  const currentPrices = props.prices[props.interval][currency]

  const planStyles = getMembershipTierConfig(props.tier)

  // Use state machine Hook to calculate the current card state
  const cardState = useMembershipCardState({
    planTier: props.tier,
    planId: props.id,
    interval: props.interval,
    enableCancel: props.enableCancel,
    membership,
    subscription,
  })

  // Use action Hook
  const { executeAction } = useMembershipCardActions()

  // Calculate prices for payment
  const calculatePrices = useCallback(() => {
    const intervalPrices = props.prices[props.interval]
    return Object.entries(intervalPrices).reduce(
      (acc, [currency, price]) => ({
        ...acc,
        [currency]:
          props.interval === Interval.Month ? price.perMonth : price.perYear,
      }),
      {} as Record<Currency, number>,
    )
  }, [props.prices, props.interval])

  // Main action handler function
  const handleAction = useCallback(async () => {
    // Build action context
    const actionContext: SubscriptionInfo = {
      planId: props.id,
      tier: props.tier,
      interval: props.interval,
      prices: calculatePrices(),
      oneTimeMembership: props.oneTimeMembership,
      isActive: Boolean(subscription?.status),
      shouldDiscount: props.shouldDiscount,
      trackingOpts: {
        upgradeFromTier: membership?.tier,
        discountType: props.trackingArgs?.discountType,
      },
      extraParamsForSubscribeRedirection:
        props.extraParamsForSubscribeRedirection,
      afterCancelSurvey: props.afterCancelSurvey,
      subscription,
    }

    // Execute action corresponding to state
    await executeAction(cardState.stateAction, cardState.state, actionContext)
  }, [
    cardState.stateAction,
    cardState.state,
    executeAction,
    props,
    calculatePrices,
    membership,
    subscription,
  ])

  // CSS variables setup
  const cssVariables = useMemo(
    () =>
      ({
        '--plan-bg-color': planStyles.color,
        '--plan-bg-color-dark': planStyles.darkColor,
        '--accent-color': '#9333ea',
        '--accent-color-dark': '#a855f7',
        '--accent-color-text': '#FFF',
        '--accent-color-text-dark': '#FFF',
        '--accent-hover-color': '#9333ea0A',
        '--accent-color-highlight': '#9333ea',
        '--accent-color-highlight-dark': '#a855f7',
        ...props.style,
      }) as CSSProperties,
    [planStyles, props.style],
  )

  return (
    <section
      className={cx(
        'flex flex-col bg-white dark:bg-background rounded-2xl ring-1 ring-black/10 dark:ring-black/20 shadow-lg dark:shadow-black/20 divide-y divide-black/10 dark:divide-white/10 relative',
        props.featured && 'mmd:-mt-8',
        props.className,
      )}
      style={cssVariables}
    >
      {/* Featured badge */}
      {props.featured && <FeaturedBadge />}

      {/* Price and label area */}
      <MembershipPriceAndLabel
        label={planStyles.label}
        currency={currency}
        prices={currentPrices}
        interval={props.interval}
        tier={props.tier}
        featured={props.featured}
        discountComponent={props.discountComponent}
        cardState={cardState}
      />

      {/* Content area */}
      <div className="flex-1 flex flex-col bg-white dark:bg-white/5 rounded-b-2xl">
        {/* Privileges list */}
        <MembershipPrivilegesList
          privileges={props.privileges}
          expanded={props.expanded}
          setExpanded={props.setExpanded}
        />

        {/* State-driven button */}
        <MembershipCardButton
          cardState={cardState}
          onAction={handleAction}
          trackingArgs={props.trackingArgs}
        />
      </div>
    </section>
  )
}
